import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Send as SendIcon,
  Visibility as ViewIcon,
  Assignment as AssignmentIcon 
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const CaseManager = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [firms, setFirms] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [currentCase, setCurrentCase] = useState({
    title: '',
    firm_id: '',
    questionnaire_id: '',
    review_period_start: new Date(),
    review_period_end: new Date(),
    first_reviewer_id: '',
    second_reviewer_id: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Status colors for chips
  const statusColors = {
    'preparation': 'default',
    'sent_to_client': 'info',
    'client_submitted': 'warning',
    'first_review': 'primary',
    'second_review': 'primary',
    'final_review': 'secondary',
    'draft_report': 'secondary',
    'completed': 'success'
  };

  // Status labels for display
  const statusLabels = {
    'preparation': 'Preparation',
    'sent_to_client': 'Sent to Client',
    'client_submitted': 'Client Submitted',
    'first_review': 'First Review',
    'second_review': 'Second Review',
    'final_review': 'Final Review',
    'draft_report': 'Draft Report',
    'completed': 'Completed'
  };

  // Fetch cases and reference data on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      console.log('Fetching audit case data...');
      console.log('Token:', localStorage.getItem('token'));
      
      try {
        // Fetch all required data in parallel
        console.log('API URL:', process.env.REACT_APP_API_URL);
        
        // Fetch cases first to debug the issue
        try {
          console.log('Attempting to fetch cases...');
          const casesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/audit/cases`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          console.log('Cases response:', casesResponse.data);
          setCases(casesResponse.data);
        } catch (caseErr) {
          console.error('Error fetching cases:', caseErr);
          console.log('Response:', caseErr.response ? caseErr.response.data : 'No response data');
          setError('Failed to load cases. ' + (caseErr.response?.data?.message || caseErr.message));
        }
        
        // Continue with other data
        try {
          // Fetch each data type separately with better error handling
          try {
            const firmsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/audit/firms`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            setFirms(firmsResponse.data);
            console.log('Firms loaded:', firmsResponse.data);
          } catch (firmErr) {
            console.error('Error fetching firms:', firmErr);
            console.log('Firms response:', firmErr.response ? firmErr.response.data : 'No response data');
          }
          
          try {
            // Use the correct API endpoint for questionnaires
            const questionnairesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/audit/questionnaires`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            // Ensure questionnaires is an array
            const questionnairesData = Array.isArray(questionnairesResponse.data) ? 
              questionnairesResponse.data : 
              (questionnairesResponse.data?.data && Array.isArray(questionnairesResponse.data.data)) ? 
                questionnairesResponse.data.data : [];
            setQuestionnaires(questionnairesData);
            console.log('Questionnaires loaded:', questionnairesData);
          } catch (qErr) {
            console.error('Error fetching questionnaires:', qErr);
            console.log('Questionnaires response:', qErr.response ? qErr.response.data : 'No response data');
            
            // Fallback: try the non-prefixed endpoint if the audit-prefixed one fails
            try {
              const fallbackResponse = await axios.get(`${process.env.REACT_APP_API_URL}/questionnaires`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
              });
              // Ensure questionnaires is an array
              const questionnairesData = Array.isArray(fallbackResponse.data) ? 
                fallbackResponse.data : 
                (fallbackResponse.data?.data && Array.isArray(fallbackResponse.data.data)) ? 
                  fallbackResponse.data.data : [];
              setQuestionnaires(questionnairesData);
              console.log('Questionnaires loaded from fallback:', questionnairesData);
            } catch (fbErr) {
              console.error('Error fetching questionnaires from fallback:', fbErr);
            }
          }
          
          try {
            const reviewersResponse = await axios.get(`${process.env.REACT_APP_API_URL}/audit/reviewers`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            // Filter reviewers (users with reviewer role)
            const reviewerUsers = reviewersResponse.data.filter(user => 
              user.role === 'reviewer' || user.role === 'manager' || user.role === 'administrator'
            );
            setReviewers(reviewerUsers);
            console.log('Reviewers loaded:', reviewerUsers);
          } catch (revErr) {
            console.error('Error fetching reviewers:', revErr);
            console.log('Reviewers response:', revErr.response ? revErr.response.data : 'No response data');
            
            // Fallback to admin/users if the reviewers endpoint fails
            try {
              const fallbackResponse = await axios.get(`${process.env.REACT_APP_API_URL}/admin/users`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
              });
              const reviewerUsers = fallbackResponse.data.filter(user => 
                user.role === 'reviewer' || user.role === 'manager' || user.role === 'administrator'
              );
              setReviewers(reviewerUsers);
              console.log('Reviewers loaded from fallback:', reviewerUsers);
            } catch (fbErr) {
              console.error('Error fetching reviewers from fallback:', fbErr);
            }
          }
          
          // Variables are now set in the individual try/catch blocks above
        } catch (refErr) {
          console.error('Error fetching reference data:', refErr);
          console.log('Response:', refErr.response ? refErr.response.data : 'No response data');
          // Don't set error here, as we might have cases data already
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle dialog open for adding a new case
  const handleAddCase = () => {
    setCurrentCase({
      title: '',
      firm_id: '',
      questionnaire_id: '',
      review_period_start: new Date(),
      review_period_end: new Date(),
      first_reviewer_id: '',
      second_reviewer_id: ''
    });
    setDialogMode('add');
    setOpenDialog(true);
  };

  // Handle dialog open for editing an existing case
  const handleEditCase = (auditCase) => {
    setCurrentCase({
      ...auditCase,
      review_period_start: new Date(auditCase.review_period_start),
      review_period_end: new Date(auditCase.review_period_end)
    });
    setDialogMode('edit');
    setOpenDialog(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentCase(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle date changes
  const handleDateChange = (name, date) => {
    setCurrentCase(prev => ({
      ...prev,
      [name]: date
    }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      let response;
      
      if (dialogMode === 'add') {
        response = await axios.post(`${process.env.REACT_APP_API_URL}/audit/cases`, currentCase, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        setSnackbar({
          open: true,
          message: 'Case created successfully',
          severity: 'success'
        });
      } else {
        response = await axios.put(`${process.env.REACT_APP_API_URL}/audit/cases/${currentCase.id}`, currentCase, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        setSnackbar({
          open: true,
          message: 'Case updated successfully',
          severity: 'success'
        });
      }
      
      // Refresh cases list
      fetchCases();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving case:', err);
      setSnackbar({
        open: true,
        message: 'Error saving case. Please try again.',
        severity: 'error'
      });
    }
  };

  // Fetch cases from API
  const fetchCases = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/audit/cases`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setCases(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching cases:', err);
      setError('Failed to load cases. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle case deletion
  const handleDeleteCase = async (id) => {
    if (window.confirm('Are you sure you want to delete this case? This action cannot be undone.')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/audit/cases/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        // Refresh cases list
        fetchCases();
        
        setSnackbar({
          open: true,
          message: 'Case deleted successfully',
          severity: 'success'
        });
      } catch (err) {
        console.error('Error deleting case:', err);
        setSnackbar({
          open: true,
          message: err.response?.data?.message || 'Error deleting case. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  // Handle sending questionnaire to client
  const handleSendToClient = async (id) => {
    if (window.confirm('Are you sure you want to send this questionnaire to the client?')) {
      try {
        await axios.post(`${process.env.REACT_APP_API_URL}/audit/cases/${id}/send`, {}, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        // Refresh cases list
        fetchCases();
        
        setSnackbar({
          open: true,
          message: 'Questionnaire sent to client successfully',
          severity: 'success'
        });
      } catch (err) {
        console.error('Error sending questionnaire:', err);
        setSnackbar({
          open: true,
          message: err.response?.data?.message || 'Error sending questionnaire. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  // Handle view case details
  const handleViewCase = (id) => {
    navigate(`/audit/cases/${id}`);
  };

  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  // Get firm name by ID
  const getFirmName = (firmId) => {
    const firm = firms.find(f => f.id === firmId);
    return firm ? firm.name : 'Unknown';
  };

  // Get questionnaire title by ID
  const getQuestionnaireName = (questionnaireId) => {
    const questionnaire = questionnaires.find(q => q.id === questionnaireId);
    return questionnaire ? questionnaire.title : 'Unknown';
  };

  // Get reviewer name by ID
  const getReviewerName = (reviewerId) => {
    const reviewer = reviewers.find(r => r.id === reviewerId);
    return reviewer ? reviewer.name : 'Unknown';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Case Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddCase}
        >
          Create Case
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Firm</TableCell>
                <TableCell>Questionnaire</TableCell>
                <TableCell>Review Period</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cases.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No cases found. Click "Create Case" to create one.
                  </TableCell>
                </TableRow>
              ) : (
                cases.map((auditCase) => (
                  <TableRow key={auditCase.id}>
                    <TableCell>{auditCase.title}</TableCell>
                    <TableCell>{getFirmName(auditCase.firm_id)}</TableCell>
                    <TableCell>{getQuestionnaireName(auditCase.questionnaire_id)}</TableCell>
                    <TableCell>
                      {new Date(auditCase.review_period_start).toLocaleDateString()} - {new Date(auditCase.review_period_end).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={statusLabels[auditCase.status]} 
                        color={statusColors[auditCase.status]} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        color="primary" 
                        onClick={() => handleViewCase(auditCase.id)}
                        title="View case details"
                      >
                        <ViewIcon />
                      </IconButton>
                      {auditCase.status === 'preparation' && (
                        <>
                          <IconButton 
                            color="primary" 
                            onClick={() => handleEditCase(auditCase)}
                            title="Edit case"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            color="error" 
                            onClick={() => handleDeleteCase(auditCase.id)}
                            title="Delete case"
                          >
                            <DeleteIcon />
                          </IconButton>
                          <IconButton 
                            color="success" 
                            onClick={() => handleSendToClient(auditCase.id)}
                            title="Send to client"
                          >
                            <SendIcon />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Case Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? 'Create New Case' : 'Edit Case'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="title"
              label="Case Title"
              name="title"
              value={currentCase.title}
              onChange={handleInputChange}
            />
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Firm</InputLabel>
                  <Select
                    name="firm_id"
                    value={currentCase.firm_id}
                    onChange={handleInputChange}
                    label="Firm"
                  >
                    <MenuItem value="">
                      <em>Select a firm</em>
                    </MenuItem>
                    {firms.map(firm => (
                      <MenuItem key={firm.id} value={firm.id}>
                        {firm.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Questionnaire</InputLabel>
                  <Select
                    name="questionnaire_id"
                    value={currentCase.questionnaire_id}
                    onChange={handleInputChange}
                    label="Questionnaire"
                  >
                    <MenuItem value="">
                      <em>Select a questionnaire</em>
                    </MenuItem>
                    {Array.isArray(questionnaires) ? questionnaires.map(questionnaire => (
                      <MenuItem key={questionnaire.id} value={questionnaire.id}>
                        {questionnaire.title || 'Untitled Questionnaire'}
                      </MenuItem>
                    )) : <MenuItem value=""><em>No questionnaires available</em></MenuItem>}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Review Period Start"
                    value={currentCase.review_period_start}
                    onChange={(date) => handleDateChange('review_period_start', date)}
                    renderInput={(params) => <TextField {...params} fullWidth margin="normal" required />}
                  />
                </LocalizationProvider>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Review Period End"
                    value={currentCase.review_period_end}
                    onChange={(date) => handleDateChange('review_period_end', date)}
                    renderInput={(params) => <TextField {...params} fullWidth margin="normal" required />}
                    minDate={currentCase.review_period_start}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>First Reviewer</InputLabel>
                  <Select
                    name="first_reviewer_id"
                    value={currentCase.first_reviewer_id}
                    onChange={handleInputChange}
                    label="First Reviewer"
                  >
                    <MenuItem value="">
                      <em>Select first reviewer</em>
                    </MenuItem>
                    {reviewers.map(reviewer => (
                      <MenuItem 
                        key={reviewer.id} 
                        value={reviewer.id}
                        disabled={reviewer.id === currentCase.second_reviewer_id}
                      >
                        {reviewer.name} ({reviewer.role})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Second Reviewer</InputLabel>
                  <Select
                    name="second_reviewer_id"
                    value={currentCase.second_reviewer_id}
                    onChange={handleInputChange}
                    label="Second Reviewer"
                  >
                    <MenuItem value="">
                      <em>Select second reviewer</em>
                    </MenuItem>
                    {reviewers.map(reviewer => (
                      <MenuItem 
                        key={reviewer.id} 
                        value={reviewer.id}
                        disabled={reviewer.id === currentCase.first_reviewer_id}
                      >
                        {reviewer.name} ({reviewer.role})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {dialogMode === 'add' ? 'Create Case' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CaseManager;
